/**
 * Dealer context — the dealer a shopper has chosen, and the parts they want to collect there.
 *
 * The same shape as assets/trailer-context.js, and for the same reason: a fact the shopper
 * gave us once should follow them across the site without being asked again. One localStorage
 * key holds the chosen dealer, another the collect list; every mutation broadcasts
 * `dealer:changed` so each consumer re-renders itself.
 *
 * window.ValerydDealer
 *   read()                      → { dealer, savedAt } | null
 *   choose(dealer)              → remember a dealer (the proxy's own store object)
 *   forget()                    → clear the dealer AND the list
 *   list()                      → [{ sku, title, url, quantity }]
 *   add(item) / remove(sku)     → change the collect list
 *   clearList()
 *   count()                     → items on the list
 *   country(el)                 → Promise<ISO2 | null>: whose dealers `el` may show
 *
 * country() is the client half of snippets/dealer-country.liquid, and the only one. If the
 * market pinned a dealer country, the server rendered it into `data-dealer-country`, and that
 * is the answer without a request. Otherwise only a visitor whose own country (by IP, through
 * ValerydGeo) is named in `data-dealer-geo` sees their dealers. Everything else resolves to
 * null, which means show nothing: no list, no geo, an unsupported country, a failed lookup.
 * There is no default country. A Swedish default is how a German shopper whose lookup timed out
 * used to be shown Swedish dealers.
 *
 * Apart from that one ValerydGeo call, nothing here talks to the network. Stock at the chosen
 * dealer is looked up per article by <collect-list> through the same App Proxy the availability panel uses, because the answer
 * changes and must never be cached into a promise we cannot keep: dealers are independent
 * resellers, so their stock is information we display, never something we reserve.
 */
(function () {
  if (window.ValerydDealer) return;

  var DEALER_KEY = 'valeryd:dealer:v1';
  var LIST_KEY = 'valeryd:collect:v1';
  var EVENT = 'dealer:changed';
  var MAX_ITEMS = 20;

  function readJson(key) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeJson(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* storage full or blocked — the feature simply stays off */
    }
  }

  function announce() {
    document.dispatchEvent(new CustomEvent(EVENT));
  }

  var store = {
    read: function () {
      var saved = readJson(DEALER_KEY);
      return saved && saved.dealer && saved.dealer.name ? saved : null;
    },

    choose: function (dealer) {
      if (!dealer || !dealer.name) return;
      writeJson(DEALER_KEY, { savedAt: Date.now(), dealer: dealer });
      // For GTM (docs/analytics-tracking.md). The country, not the dealer: a report on which
      // reseller gets chosen is not ours to keep.
      if (window.dataLayer) window.dataLayer.push({ event: 'dealer_select', dealer_country: dealer.country || '' });
      announce();
    },

    forget: function () {
      writeJson(DEALER_KEY, null);
      writeJson(LIST_KEY, null);
      announce();
    },

    list: function () {
      var items = readJson(LIST_KEY);
      return Array.isArray(items) ? items.filter(function (i) { return i && i.sku; }) : [];
    },

    add: function (item) {
      if (!item || !item.sku) return;
      var items = store.list();
      var sku = String(item.sku).trim().toUpperCase();
      var found = items.find(function (i) { return i.sku === sku; });
      if (found) found.quantity = (Number(found.quantity) || 1) + (Number(item.quantity) || 1);
      else if (items.length < MAX_ITEMS) {
        items.push({ sku: sku, title: item.title || sku, url: item.url || '', quantity: Number(item.quantity) || 1 });
      }
      writeJson(LIST_KEY, items);
      announce();
    },

    remove: function (sku) {
      var key = String(sku || '').trim().toUpperCase();
      writeJson(LIST_KEY, store.list().filter(function (i) { return i.sku !== key; }));
      announce();
    },

    clearList: function () {
      writeJson(LIST_KEY, null);
      announce();
    },

    count: function () {
      return store.list().reduce(function (n, i) { return n + (Number(i.quantity) || 1); }, 0);
    },

    has: function (sku) {
      var key = String(sku || '').trim().toUpperCase();
      return store.list().some(function (i) { return i.sku === key; });
    },

    country: function (el) {
      var data = (el && el.dataset) || {};
      var pinned = String(data.dealerCountry || '').trim().toUpperCase();
      if (pinned) return Promise.resolve(pinned);
      var allowed = String(data.dealerGeo || '')
        .split(',')
        .map(function (code) { return code.trim().toUpperCase(); })
        .filter(Boolean);
      if (!allowed.length || !window.ValerydGeo) return Promise.resolve(null);
      return window.ValerydGeo.resolve(data.geoEndpoint).then(function (geo) {
        return geo && geo.supported !== false && allowed.indexOf(geo.country) !== -1 ? geo.country : null;
      });
    },
  };

  window.ValerydDealer = store;
})();
